import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole, isTopTier } from "@/lib/permissions";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, Pencil, Airplane, Upload, Key, MagnifyingGlass } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";

/**
 * UsersAdmin — single source of truth for user CRUD.
 * Mirrors the Admin & Settings → Users tab exactly. Used in two places:
 *   1) /admin (Users tab) for full admin access.
 *   2) /users/new (sidebar "New User") for supervisor / admin / commanding_officer.
 * Any field/validation/role change made here automatically applies to both
 * surfaces, preventing drift between "Add User" entry points.
 *
 * Props
 * ─────
 * autoOpen  → open the create dialog as soon as the component mounts.
 * onCreated → optional callback fired after a successful create or edit.
 */
// Every role that can be assigned. UI now uses a single unified multi-select
// (see `rolesInForm` in the component) instead of a Primary dropdown + a
// separate Additional Roles list — the first item still becomes the user's
// `role` and the rest become `additional_roles`, but the operator only sees
// one control.
const ROLES = ["volunteer", "supervisor", "admin", "staff_chief", "commanding_officer", "super_admin"];
// Roles an Admin may assign on an Add/Edit User flow. Elevated roles
// (admin, staff_chief, commanding_officer, super_admin) stay exclusive to
// top-tier callers; Admin is scoped to the operational tier only.
// Logistics is NOT a role — it's a certification (checkbox under
// Authorized Certifications). Assigning Logistics happens there.
const ADMIN_ASSIGNABLE_ROLES = ["volunteer", "supervisor"];
// Roles only a top-tier user may hand out — mutating any of these
// requires CO / Super Admin / Staff Chief. Must stay in sync with the
// backend `_ELEVATED_ROLES` guard in users_routes.py.
const ELEVATED_ROLES = new Set(["admin", "staff_chief", "commanding_officer", "super_admin"]);
// Human labels + descriptions used in the checkbox group.
const ROLE_META = {
  volunteer:          { label: "Volunteer",          hint: "Baseline member" },
  supervisor:         { label: "Supervisor",         hint: "Schedule + Supervisor Hub" },
  admin:              { label: "Admin",              hint: "Admin area, user management" },
  staff_chief:        { label: "Staff Chief",        hint: "Full access minus Validation Tool" },
  commanding_officer: { label: "Commanding Officer", hint: "Full access · no restrictions" },
  super_admin:        { label: "Super Admin",        hint: "Full access · no restrictions" },
};
const PRIMARY_ORDER = ["Supervisor", "Paramedic", "EMT", "Driver", "Trainee"];
const DEFAULT_NEW_USER_PASSWORD = "bsvac727";
// `role` now holds the *first* selected role; the trailing roles land in
// `additional_roles`. The form still keeps them split so the payload matches
// the existing backend contract without a schema change.
const EMPTY_FORM = { email: "", password: "", name: "", role: "volunteer", additional_roles: [], certification: "", certifications: [], ems_certification: "", phone: "" };

export default function UsersAdmin({ autoOpen = false, onCreated }) {
  const { user: currentUser } = useAuth();
  // Role-assignment matrix:
  //   Top-tier (CO / Super Admin / Staff Chief, or the seeded Original
  //     Admin) → every role, including promoting someone to elevated.
  //   Admin                                      → operational roles only
  //     [volunteer, supervisor]. Logistics is a cert, not a role.
  //   Supervisor and below                       → volunteer only.
  const canAssignElevatedRoles = isTopTier(currentUser);
  const canAssignOperationalRoles = canAssignElevatedRoles || hasRole(currentUser, "admin");
  const visibleRoles = canAssignElevatedRoles
    ? ROLES
    : canAssignOperationalRoles
      ? ADMIN_ASSIGNABLE_ROLES
      : ["volunteer"];
  // Navigate hook for the row-level "Edit" button, which now opens the
  // member's profile page in Edit Mode rather than firing a separate
  // Dialog (iter34 — profile is the single source of truth for editing).
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Free-text search across the roster — matches name, email, username, role
  // (primary + additional), certifications, and phone.
  const [query, setQuery] = useState(() => sessionStorage.getItem("usersAdmin.query") || "");
  // Bulk-import (WhenToHelp copy-paste). Only surfaced for real admins.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const canBulkImport = hasRole(currentUser, "admin", "staff_chief", "commanding_officer", "super_admin");
  // Retired per iter21 — EMT/Paramedic moved to a dedicated EMS Cert
  // selector; MVO is now driven by driver_clearance_date on the profile;
  // Intern variants are handled through the clinical clearance status.
  const RETIRED_CATALOG = new Set(["emt", "paramedic", "mvo", "intern", "emt intern", "paramedic intern", "dispatcher", "volunteer"]);
  const catalog = (settings?.certifications_catalog || ["Trainee", "Driver", "Supervisor"])
    .filter((c) => !RETIRED_CATALOG.has(String(c).trim().toLowerCase()));

  /**
   * Parse a WhenToHelp-style paste into `{first_name,last_name,phone,email}`
   * rows. WhenToHelp exports as tab-separated on copy from its table view,
   * so tabs are the primary delimiter with commas / pipes / multi-space
   * as fallbacks. Detects a header row by looking for "email" in the
   * first line and drops it. Skips blank lines. Any column can be missing
   * — validation happens server-side.
   */
  function parseBulkPaste(raw) {
    const lines = String(raw || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    // Auto-detect delimiter: prefer tab, then pipe, then comma. Fall back
    // to 2-or-more-spaces so a hand-formatted paste still works.
    const first = lines[0];
    let delim = "\t";
    if (!first.includes("\t")) {
      if (first.includes("|")) delim = "|";
      else if (first.includes(",")) delim = ",";
      else delim = /\s{2,}/;
    }
    const looksHeader = /email/i.test(first) && /(name|first|last|phone)/i.test(first);
    const dataLines = looksHeader ? lines.slice(1) : lines;
    return dataLines.map((line) => {
      const parts = (delim instanceof RegExp)
        ? line.split(delim).map((p) => p.trim())
        : line.split(delim).map((p) => p.trim());
      // Column order per BSVAC brief: First Name, Last Name, Phone, Email.
      // If someone pastes "First Last, phone, email" (3 columns), split
      // the first cell on whitespace.
      let first_name = "", last_name = "", phone = "", email = "";
      if (parts.length >= 4) {
        [first_name, last_name, phone, email] = parts;
      } else if (parts.length === 3) {
        const [nameCell, p2, p3] = parts;
        const nameParts = nameCell.split(/\s+/);
        first_name = nameParts[0] || "";
        last_name = nameParts.slice(1).join(" ");
        phone = p2;
        email = p3;
      } else if (parts.length === 2) {
        // "Full Name  email"
        const nameParts = parts[0].split(/\s+/);
        first_name = nameParts[0] || "";
        last_name = nameParts.slice(1).join(" ");
        email = parts[1];
      } else {
        first_name = parts[0] || "";
      }
      // Look for a valid email in any column if the last one isn't it —
      // WhenToHelp sometimes reorders on paste.
      if (!/@/.test(email)) {
        const found = parts.find((p) => /@/.test(p));
        if (found) email = found;
      }
      return { first_name, last_name, phone, email: (email || "").toLowerCase() };
    });
  }
  const bulkPreview = parseBulkPaste(bulkRaw);
  const bulkValid = bulkPreview.filter((r) => /@/.test(r.email || "") && (r.first_name || r.last_name));

  async function runBulkImport() {
    if (bulkBusy) return;
    if (!bulkValid.length) {
      toast.error("Nothing to import — need at least one row with a name and an email");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk-import", { rows: bulkPreview });
      const s = `${data.created} created · ${data.skipped} skipped of ${data.total} rows`;
      if (data.created > 0) toast.success(s); else toast.error(s);
      if (data.skipped > 0) {
        // Console-log skipped rows so the admin can debug if needed —
        // most common cause is a duplicate email.
        // eslint-disable-next-line no-console
        console.warn("Bulk import skips:", data.results.filter((r) => r.status === "skipped"));
      }
      setBulkRaw("");
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  }

  // Sort roster by first name (given name) — the token before the first
  // space. Falls back to the full name for single-word display names.
  function firstNameKey(u) {
    return String(u.name || "").trim().split(/\s+/)[0].toLowerCase();
  }
  const sortedUsers = useMemo(() => {
    const arr = [...users].sort((a, b) => firstNameKey(a).localeCompare(firstNameKey(b)));
    const needle = query.trim().toLowerCase();
    if (!needle) return arr;
    return arr.filter((u) => {
      const roleTokens = [u.role, ...(u.additional_roles || [])].filter(Boolean).join(" ");
      const certTokens = ((u.certifications && u.certifications.length ? u.certifications : [u.certification]).filter(Boolean)).join(" ");
      const hay = [u.name, u.email, u.username, u.phone, roleTokens, certTokens]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [users, query]);

  async function load() {
    const [u, s] = await Promise.all([
      api.get("/users/"),
      api.get("/settings/").catch(() => ({ data: null })),
    ]);
    setUsers(u.data);
    setSettings(s.data);
  }
  useEffect(() => { load(); }, []);
  // iter34 — Persist the search query across route transitions. The
  // scroll position is restored by the browser's own history-restoration
  // once the user list has rendered; we trigger that after `users`
  // settles below.
  useEffect(() => {
    sessionStorage.setItem("usersAdmin.query", query);
  }, [query]);
  // Save the current scroll position whenever the admin navigates away
  // (specifically to a member profile via the row-level Edit button) so
  // Back returns them to the exact row they clicked from — not the top
  // of the list. We write on every scroll into sessionStorage so even a
  // hard refresh preserves the position.
  useEffect(() => {
    const key = "usersAdmin.scrollY";
    const onScroll = () => sessionStorage.setItem(key, String(window.scrollY || 0));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Restore scroll AFTER the user list is populated — otherwise the
  // page is still short and scrollTo clamps to 0. We only restore once
  // per mount so toggling filters doesn't yank the view back.
  const scrollRestoredRef = React.useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (!users || users.length === 0) return;
    const stored = sessionStorage.getItem("usersAdmin.scrollY");
    if (!stored) return;
    const y = Number(stored);
    if (Number.isFinite(y) && y > 0) {
      // defer one frame so the table layout settles before we scroll
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
    scrollRestoredRef.current = true;
  }, [users]);
  useEffect(() => {
    if (autoOpen) { openCreate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      additional_roles: Array.isArray(u.additional_roles) ? u.additional_roles : [],
      certification: u.certification || "",
      certifications: (u.certifications && u.certifications.length ? u.certifications : (u.certification ? [u.certification] : []))
        .filter((c) => !["emt", "paramedic", "mvo", "intern", "emt intern", "paramedic intern", "dispatcher", "volunteer"].includes(String(c).trim().toLowerCase())),
      ems_certification: u.ems_certification || "",
      phone: u.phone || "",
    });
    setOpen(true);
  }
  function toggleCert(c) {
    setForm((f) => ({ ...f, certifications: f.certifications.includes(c) ? f.certifications.filter((x) => x !== c) : [...f.certifications, c] }));
  }
  // Unified role selection. Keeps `role` as the "first" chosen role and
  // pushes the rest into `additional_roles` so the backend contract still
  // holds. Ordering follows the ROLES constant so the primary role stays
  // stable regardless of the order the operator clicks the checkboxes in.
  const rolesInForm = [form.role, ...(form.additional_roles || [])].filter(Boolean);
  function toggleRole(r) {
    setForm((f) => {
      const owned = new Set([f.role, ...(f.additional_roles || [])].filter(Boolean));
      if (owned.has(r)) {
        owned.delete(r);
      } else {
        owned.add(r);
      }
      // Always keep at least Volunteer so the user has a valid primary role.
      if (owned.size === 0) owned.add("volunteer");
      const ordered = ROLES.filter((x) => owned.has(x));
      return { ...f, role: ordered[0], additional_roles: ordered.slice(1) };
    });
  }
  async function save(e) {
    e.preventDefault();
    // iter21 — Authorized Certifications may legitimately be empty for
    // members with no secondary role (their EMS cert alone is enough).
    // Primary cert is picked from the authorized list if present; else
    // falls back to the EMS cert so required-docs stays correct.
    const primary = (form.certifications.length
      ? (PRIMARY_ORDER.find((c) => form.certifications.includes(c)) || form.certifications[0])
      : (form.ems_certification || ""));
    // Role mutation rules (mirrored on the backend by _ELEVATED_ROLES
    // guard). A non-top-tier caller may only toggle roles inside their
    // allowed set; they must NOT be able to add or remove elevated roles.
    // When editing an existing member who already has elevated roles,
    // preserve those exactly so an Admin editing a Staff Chief does not
    // accidentally demote them on save.
    let safeRole;
    let safeExtras;
    if (canAssignElevatedRoles) {
      safeRole = form.role;
      safeExtras = (form.additional_roles || []).filter((r) => r && r !== safeRole);
    } else if (canAssignOperationalRoles) {
      // Preserve any elevated roles already on the record so this patch
      // can't demote them. Form roles are filtered to the operational
      // allow-list so stray elevated values from a stale form never go up.
      const preserved = editing
        ? [editing.role, ...(editing.additional_roles || [])].filter((r) => r && ELEVATED_ROLES.has(r))
        : [];
      const operational = [form.role, ...(form.additional_roles || [])]
        .filter((r) => r && ADMIN_ASSIGNABLE_ROLES.includes(r));
      const merged = Array.from(new Set([...operational, ...preserved]));
      // Preserve existing primary when it was elevated — never let an
      // Admin flip the primary role of a Staff Chief / CO.
      if (editing && ELEVATED_ROLES.has(editing.role)) {
        safeRole = editing.role;
        safeExtras = merged.filter((r) => r !== safeRole);
      } else {
        const ordered = ROLES.filter((r) => merged.includes(r));
        safeRole = ordered[0] || "volunteer";
        safeExtras = ordered.slice(1);
      }
    } else {
      safeRole = "volunteer";
      safeExtras = [];
    }
    try {
      if (editing) {
        const payload = { name: form.name, role: safeRole, certification: primary, certifications: form.certifications, ems_certification: form.ems_certification || "", phone: form.phone };
        payload.additional_roles = safeExtras;
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        const payload = { ...form, role: safeRole, additional_roles: safeExtras, password: DEFAULT_NEW_USER_PASSWORD, certification: primary, ems_certification: form.ems_certification || null };
        await api.post("/users/", payload);
        toast.success("User created");
      }
      setOpen(false);
      load();
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }
  async function remove(u) {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  }
  async function toggleActive(u) {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  const [loaTarget, setLoaTarget] = useState(null);
  async function resetPassword(u) {
    // Admin safety net — resets a member's login back to `bsvac727`
    // with must_change_password=True. Useful after a bulk import where
    // the member never received their credentials, or when someone
    // locks themselves out.
    if (!window.confirm(`Reset ${u.name}'s password to bsvac727? They'll be forced to change it on their next login.`)) return;
    try {
      await api.post(`/users/${u.id}/reset-password`);
      toast.success(`Password reset for ${u.name} — they can now log in with bsvac727`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  }

  async function endLoa(u) {
    if (!window.confirm(`End LOA for ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}/loa`);
      toast.success("LOA ended");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search members by name, email, role, cert…"
            className="rounded-sm pl-7"
            data-testid="user-search"
          />
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
        {canBulkImport && (
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="user-bulk-import-btn">
                <Upload size={14} weight="bold" className="mr-1" />Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Bulk Import Members (paste from WhenToHelp)</DialogTitle></DialogHeader>
              <div className="text-xs text-slate-600 space-y-1">
                <div>Copy from WhenToHelp (or a spreadsheet) and paste below. Expected columns:</div>
                <div className="mono bg-slate-100 rounded-sm px-2 py-1 text-slate-800">First Name &nbsp; Last Name &nbsp; Phone &nbsp; Email</div>
                <div>Tabs, pipes, commas, and multi-space are all detected automatically. A header row is optional. New members get role <b>volunteer</b>, cert <b>EMT</b>, and password <b>bsvac727</b> — they'll be forced to change it on first login.</div>
              </div>
              <Textarea
                value={bulkRaw}
                onChange={(e) => setBulkRaw(e.target.value)}
                className="rounded-sm mono text-xs mt-2"
                rows={8}
                placeholder={"Jane\tDoe\t555-123-4567\tjane@example.org\nJohn\tSmith\t555-987-6543\tjohn@example.org"}
                data-testid="user-bulk-textarea"
              />
              {bulkPreview.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-sm overflow-hidden">
                  <div className="bg-slate-100 px-2 py-1 flex justify-between items-center">
                    <span className="tab-tag text-slate-600">Preview</span>
                    <span className="text-[11px] text-slate-500" data-testid="user-bulk-preview-count">
                      {bulkValid.length} ready · {bulkPreview.length - bulkValid.length} incomplete
                    </span>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr><th className="text-left px-2 py-1">First</th><th className="text-left px-2 py-1">Last</th><th className="text-left px-2 py-1">Phone</th><th className="text-left px-2 py-1">Email</th></tr>
                      </thead>
                      <tbody>
                        {bulkPreview.slice(0, 100).map((r, i) => {
                          const ok = /@/.test(r.email || "") && (r.first_name || r.last_name);
                          return (
                            <tr key={i} className={ok ? "" : "bg-red-50"}>
                              <td className="px-2 py-1 border-t border-slate-100">{r.first_name || "—"}</td>
                              <td className="px-2 py-1 border-t border-slate-100">{r.last_name || "—"}</td>
                              <td className="px-2 py-1 border-t border-slate-100">{r.phone || "—"}</td>
                              <td className="px-2 py-1 border-t border-slate-100 mono">{r.email || <span className="text-red-600">missing</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" className="rounded-sm" onClick={() => { setBulkRaw(""); setBulkOpen(false); }}>Cancel</Button>
                <Button onClick={runBulkImport} disabled={bulkBusy || !bulkValid.length} className="rounded-sm bg-slate-900 text-white" data-testid="user-bulk-submit">
                  {bulkBusy ? "Importing…" : `Import ${bulkValid.length} member${bulkValid.length === 1 ? "" : "s"}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-sm bg-slate-900 text-white" data-testid="user-new">
              <Plus size={14} weight="bold" className="mr-1" />New User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="tab-tag">Name</Label><Input required className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                <div><Label className="tab-tag">Email</Label><Input required type="email" disabled={!!editing} className="rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {editing ? (
                  <div>
                    <Label className="tab-tag">New Password (optional)</Label>
                    <Input type="password" className="rounded-sm" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                  </div>
                ) : (
                  <div>
                    <Label className="tab-tag">Default Password</Label>
                    <div className="border border-emerald-300 bg-emerald-50 rounded-sm px-3 py-2 text-xs text-emerald-800" data-testid="user-default-pw-note">
                      <span className="mono font-bold">{DEFAULT_NEW_USER_PASSWORD}</span> — member must change on first login.
                    </div>
                  </div>
                )}
                <div><Label className="tab-tag">Phone</Label><Input className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone" /></div>
              </div>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <Label className="tab-tag">
                  Roles
                  {!canAssignElevatedRoles && canAssignOperationalRoles && (
                    <span className="normal-case tracking-normal text-slate-500 font-normal"> (your account can assign Volunteer and Supervisor — Logistics is a cert, assign it below)</span>
                  )}
                  {!canAssignOperationalRoles && (
                    <span className="normal-case tracking-normal text-slate-500 font-normal"> (your account can only assign Volunteer)</span>
                  )}
                </Label>
                <p className="text-[11px] text-slate-500 mb-2">
                  Check every role this member should have. All checked roles apply — permissions from each role stack together.
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="user-roles-grid">
                  {visibleRoles.map((r) => (
                    <label key={r} className="flex items-start gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer" data-testid={`user-role-${r}`}>
                      <Checkbox
                        checked={rolesInForm.includes(r)}
                        onCheckedChange={() => toggleRole(r)}
                        className="mt-0.5"
                      />
                      <div className="leading-tight">
                        <div className="capitalize font-medium text-slate-900">{ROLE_META[r]?.label || r.replace(/_/g, " ")}</div>
                        <div className="text-[10px] text-slate-500">{ROLE_META[r]?.hint || ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {/* When an Admin edits a member who already has elevated roles,
                    we preserve those roles on save but surface them as a
                    read-only badge so the operator knows exactly what sticks. */}
                {!canAssignElevatedRoles && editing && (() => {
                  const preserved = [editing.role, ...(editing.additional_roles || [])]
                    .filter((r) => r && ELEVATED_ROLES.has(r));
                  if (!preserved.length) return null;
                  return (
                    <div className="mt-2 border border-amber-300 bg-amber-50 rounded-sm px-2 py-1.5 text-[11px] text-amber-900" data-testid="user-elevated-locked">
                      <span className="font-bold">Preserved on save:</span>{" "}
                      {preserved.map((r) => ROLE_META[r]?.label || r).join(", ")}
                      {" — "}only a Staff Chief / Commanding Officer / Super Admin can change elevated roles.
                    </div>
                  );
                })()}
                {rolesInForm.length > 1 && (
                  <p className="mt-2 text-[11px] text-slate-600" data-testid="user-primary-role-note">
                    Selected: <span className="mono">{rolesInForm.map((r) => ROLE_META[r]?.label || r).join(", ")}</span>
                  </p>
                )}
              </div>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <Label className="tab-tag">EMS Certification</Label>
                <p className="text-[11px] text-slate-500 mb-2">Professional credential (optional). Required paperwork follows this selection. Intern status is set via Clinical Clearance on the profile — not here.</p>
                <div className="flex flex-wrap gap-2" data-testid="user-ems-cert-grid">
                  {[
                    { v: "", label: "None" },
                    { v: "EMT", label: "EMT" },
                    { v: "Paramedic", label: "Paramedic" },
                  ].map((o) => {
                    const active = (form.ems_certification || "") === o.v;
                    return (
                      <button
                        key={o.v || "none"}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, ems_certification: o.v }))}
                        className={`border rounded-sm px-4 py-1.5 text-sm transition-colors ${active ? "border-slate-900 bg-slate-900 text-white font-bold" : "border-slate-200 bg-white hover:bg-slate-100"}`}
                        data-testid={`user-ems-cert-${o.v || "none"}`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <Label className="tab-tag">Authorized Certifications</Label>
                <p className="text-[11px] text-slate-500 mb-2">Additional credentials beyond EMS (e.g. Dispatcher, Supervisor, Student, Trainee). Driver eligibility is managed via Driver Clearance on the profile.</p>
                <div className="grid grid-cols-2 gap-2">
                  {catalog.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer" data-testid={`user-cert-${c}`}>
                      <Checkbox checked={form.certifications.includes(c)} onCheckedChange={() => toggleCert(c)} />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter><Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="user-save">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Authorized Certs</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500" data-testid="user-empty-state">
                  {query.trim() ? `No members match "${query.trim()}"` : "No members yet."}
                </td>
              </tr>
            ) : sortedUsers.map((u) => { const locked = !canAssignElevatedRoles && [u.role, ...(u.additional_roles || [])].some((r) => ELEVATED_ROLES.has(r)); const isSelfRow = u.id === currentUser?.id; return (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2 font-medium">
                  <Link
                    to={`/members/${u.id}`}
                    className="text-slate-900 hover:text-blue-700 hover:underline"
                    data-testid={`user-open-${u.id}`}
                  >
                    {u.name}
                  </Link>
                </td>
                <td className="px-4 py-2 mono text-xs">{u.email}</td>
                <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-900 text-white uppercase tracking-wider text-[10px]">{u.role}</Badge></td>
                <td className="px-4 py-2 space-x-1 space-y-1">
                  {((u.certifications && u.certifications.length) ? u.certifications : [u.certification]).filter(Boolean).map((c) => (
                    <Badge key={c} className="rounded-sm bg-slate-700 text-white">{c}</Badge>
                  ))}
                </td>
                <td className="px-4 py-2 tab-tag">
                  {u.loa?.active ? (
                    <span className="text-slate-500 italic" data-testid={`user-loa-badge-${u.id}`}>ON LOA · {u.loa.end_date}</span>
                  ) : u.active ? (
                    <span className="text-emerald-700">ACTIVE</span>
                  ) : (
                    <span className="text-red-600">DISABLED</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right flex justify-end gap-1">
                  {locked && !isSelfRow ? (
                    <span className="tab-tag text-[10px] text-slate-500 border border-slate-300 rounded-sm px-1.5 py-1" title="Only a Staff Chief, Commanding Officer, or Super Admin can change this account" data-testid={`user-protected-${u.id}`}>PROTECTED</span>
                  ) : (<>
                  {u.loa?.active ? (
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-slate-400 text-slate-700" onClick={() => endLoa(u)} data-testid={`user-loa-end-${u.id}`}>
                      End LOA
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => setLoaTarget(u)} data-testid={`user-loa-${u.id}`}>
                      <Airplane size={12} weight="bold" className="mr-1" />LOA
                    </Button>
                  )}
                  {!(locked && isSelfRow) && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>{u.active ? "Disable" : "Enable"}</Button>}
                  {!(locked && isSelfRow) && <Button size="icon" variant="ghost" className="text-slate-700" onClick={() => resetPassword(u)} title="Reset password to bsvac727" data-testid={`user-reset-pw-${u.id}`}><Key size={14} /></Button>}
                  <Button size="icon" variant="ghost" onClick={() => nav(`/members/${u.id}?edit=1`)} title="Open member profile in Edit Mode" data-testid={`user-edit-${u.id}`}><Pencil size={14} /></Button>
                  {!(locked && isSelfRow) && <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(u)} data-testid={`user-del-${u.id}`}><Trash size={14} /></Button>}
                  </>)}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      <LOADialog user={loaTarget} onClose={() => setLoaTarget(null)} onSaved={load} />
    </>
  );
}


function LOADialog({ user, onClose, onSaved }) {
  const [mode, setMode] = useState("preset");
  const [duration, setDuration] = useState(30);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!user;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "custom"
        ? { start_date: startDate, end_date: endDate, reason }
        : { duration_days: Number(duration), reason };
      const { data } = await api.post(`/users/${user.id}/loa`, body);
      toast.success(`${user.name} placed on LOA (until ${data.loa.end_date})`);
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "LOA failed"); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="loa-dialog">
        <DialogHeader>
          <DialogTitle>Leave of Absence · {user?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="border border-slate-200 rounded-sm p-2 bg-slate-50">
            <Label className="tab-tag">Duration</Label>
            <div className="grid grid-cols-5 gap-1 mt-1">
              {[30, 60, 90, 120].map((d) => (
                <button
                  type="button"
                  key={d}
                  onClick={() => { setMode("preset"); setDuration(d); }}
                  className={`text-xs py-1.5 rounded-sm border ${mode === "preset" && duration === d ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-100"}`}
                  data-testid={`loa-preset-${d}`}
                >{d} Days</button>
              ))}
              <button
                type="button"
                onClick={() => setMode("custom")}
                className={`text-xs py-1.5 rounded-sm border ${mode === "custom" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-100"}`}
                data-testid="loa-preset-custom"
              >Custom</button>
            </div>
          </div>

          {mode === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="tab-tag">Start Date</Label>
                <Input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-sm" data-testid="loa-start" />
              </div>
              <div>
                <Label className="tab-tag">End Date</Label>
                <Input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-sm" data-testid="loa-end" />
              </div>
            </div>
          )}

          <div>
            <Label className="tab-tag">Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm" placeholder="Medical, personal, deployment…" data-testid="loa-reason" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="loa-submit">
              {busy ? "Saving…" : "Place on LOA"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}